import { useEffect, useState, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { FilterContainer } from '../../components/FilterContainer/FilterContainer';
import { KonstantinMasterHttps } from '../../fetchUrls';
import { DropDownSelector } from '@lk-gtcom/ecomlab-components';
import { Tabs } from '@lk-gtcom/ecomlab-components';
import useGeneralStore from '../../store/general';
import './Anomalies.scss';
import { WrapperLineChart } from '../../charts/WrapperCharts/WrapperLineChart/WrapperLineChart';
import { TitlePage } from '../../components/TitlePage/TitlePage';
import { options_prop_mp } from '../../common/globalVariables';
import { getValueMultiFilter } from '../../common/filtersFunc';
import { RequestController } from '../../common/filtersFunc';
import { periodWeek } from '../../common/constant';
import { useURLSearchParams } from '../../common/useURLSearchParams';
import { fetchFilterV3 } from '../../common/filtersFunc';
import { useResponseHandler } from '../../common/auth';
import { useSavedFilters } from '../../common/useSavedFilters';

const tabs = [
  {
    label: 'По товарам',
    value: 'by-product',
  },
  {
    label: 'По категории',
    value: 'by-category',
  },
  {
    label: 'По бренду',
    value: 'by_brand',
  },
  {
    label: 'По категоризации',
    value: 'by-categorization',
  },
];

const Anomalies = () => {
  const selectedSpace = useGeneralStore((state) => state.selectedSpace);
  const { handleStatusCode, handleTableResponse, handleRequestError } = useResponseHandler();
  // Сохранённые фильтры: подставляются в URL при заходе, запись по кнопке
  const { isReady, saveFilters, clearSavedFilters, isSaved, hasSavedFilters } = useSavedFilters(selectedSpace?.value, 'anomalies');
  // Реф для хранения контроллера запросов
  const requestControllerRef = useRef(new RequestController());
  const activeTab = useParams()['*']
  const location = useLocation();

  // Фильтры
  const filterConfig = [
    { key: 'mp', type: 'multi' },
    { key: 'api', type: 'multi' },
    { key: 'category', type: 'multi' },
    { key: 'brand', type: 'multi' }
  ];
  const { urlParams, updateURL, isInitialized, hasInitialParams } = useURLSearchParams(filterConfig);
  const [isFilterRequest, setIsFilterRequest] = useState(false);

  const [apiData, setApiData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [brandData, setBrandData] = useState([]);

  const [selectedMarketplace, setSelectedMarketplace] = useState([{ label: 'Все', value: 'all' }]);
  const [selectedApi, setSelectedApi] = useState([{ label: 'Все', value: 'all' }]);
  const [selectedCategory, setSelectedCategory] = useState([{ label: 'Все', value: 'all' }]);
  const [selectedBrand, setSelectedBrand] = useState([{ label: 'Все', value: 'all' }]);

  const [apiPage, setApiPage] = useState(1);
  const [categoryPage, setCategoryPage] = useState(1);
  const [brandPage, setBrandPage] = useState(1);

  const [apiQuery, setApiQuery] = useState('');
  const [categoryQuery, setCategoryQuery] = useState('');
  const [brandQuery, setBrandQuery] = useState('');

  const [totalApi, setTotalApi] = useState(0);
  const [totalCategory, setTotalCategory] = useState(0);
  const [totalBrand, setTotalBrand] = useState(0)

  const [pageValue, setPageValue] = useState(tabs[0]?.value);

  const [date, setDate] = useState(periodWeek());
  const [dateFrom, dateTo] = date;

  // Инициализация фильтров из URL параметров при первом рендере
  const [isInitialLoadFromURL, setIsInitialLoadFromURL] = useState(false);

  useEffect(() => {
    if (!isReady) return;
    if (!isInitialLoadFromURL) {
      setIsInitialLoadFromURL(true);

      // Обновляем все состояния фильтров
      setTimeout(() => {
        if (urlParams.mp) setSelectedMarketplace(urlParams.mp)
        if (urlParams.api) setSelectedApi(urlParams.api)
        if (urlParams.category) setSelectedCategory(urlParams.category)
        if (urlParams.brand) setSelectedBrand(urlParams.brand)

        // Запускаем запрос таблицы и фильтров только при первой инициализации из URL
        setIsFilterRequest(prev => !prev);
      }, 10);
    }
  }, [urlParams, isReady]);

  useEffect(() => {
    if (!isReady) return;
    updateURL({
      mp: urlParams?.mp || selectedMarketplace,
      api: urlParams?.api || selectedApi,
      category: urlParams?.category || selectedCategory,
      brand: urlParams?.brand || selectedBrand
    })
  }, [isReady]);

  const filtersApi = () => {
    const controller = requestControllerRef.current.createController('api');

    fetchFilterV3({
      selectedSpace: selectedSpace?.value,
      selectedMP: selectedMarketplace,
      page: apiPage,
      set: setApiData,
      searchParam: apiQuery,
      controller,
      setTotal: setTotalApi,
      filter: 'fq_api_ids'
    })?.finally(() => {
      requestControllerRef.current.removeController('api');
    })
  };

  const filtersCategory = () => {
    const controller = requestControllerRef.current.createController('category');

    fetchFilterV3({
      selectedSpace: selectedSpace?.value,
      selectedMP: selectedMarketplace,
      selectedApi,
      page: categoryPage,
      set: setCategoryData,
      searchParam: categoryQuery,
      controller,
      setTotal: setTotalCategory,
      filter: 'feedbacks_categories'
    })?.finally(() => {
      requestControllerRef.current.removeController('category');
    })
  };

  const filtersBrand = () => {
    const controller = requestControllerRef.current.createController('brand');

    fetchFilterV3({
      selectedSpace: selectedSpace?.value,
      selectedMP: selectedMarketplace,
      page: brandPage,
      selectedApi,
      selectedCategory,
      searchParam: brandQuery,
      set: setBrandData,
      controller,
      setTotal: setTotalBrand,
      filter: 'feedbacks_brands'
    })?.finally(() => {
      requestControllerRef.current.removeController('brand');
    })
  };

  useEffect(() => {
    if (!isInitialLoadFromURL) return;
    filtersApi();
  }, [isFilterRequest, apiPage, apiQuery, pageValue]);

  useEffect(() => {
    if (!isInitialLoadFromURL) return;
    filtersCategory();
  }, [isFilterRequest, dateFrom, dateTo, categoryPage, categoryQuery, pageValue]);

  useEffect(() => {
    if (!isInitialLoadFromURL) return;
    filtersBrand();
  }, [isFilterRequest, dateFrom, dateTo, brandPage, brandQuery, pageValue]);

  useEffect(() => {
    return () => {
      // Отменяем все активные запросы фильтров
      requestControllerRef.current.abortAll();
    };
  }, []);

  useEffect(() => {
    setPageValue(activeTab);
  }, [activeTab]);

  const body_obj = () => {
    const body = JSON.stringify({
      mp_id_list: getValueMultiFilter(selectedMarketplace),
      api_id_list: getValueMultiFilter(selectedApi),
      category_id_list: getValueMultiFilter(selectedCategory),
      brand_id_list: getValueMultiFilter(selectedBrand),
    });

    if (selectedApi?.length > 0) {
      return body;
    } else {
      return null;
    }
  };

  return (
    <div className="anomalies">
      <TitlePage
        title='Аномалии'
      />

      <FilterContainer
        loading={isSaved}
        saveFilters={saveFilters}
        clearSavedFilters={clearSavedFilters}
        hasSavedFilters={hasSavedFilters}
        onReset={(e) => {
          setSelectedMarketplace([])
          setSelectedApi([]);
          setSelectedCategory([]);
          setSelectedBrand([]);
          updateURL({
            mp: null,
            api: null,
            category: null,
            brand: null,
          });
          setIsFilterRequest(!isFilterRequest)
        }}
      >
        <DropDownSelector
          options_prop={options_prop_mp}
          state={selectedMarketplace}
          setState={setSelectedMarketplace}
          placeholder="Маркетплейс"
          total={options_prop_mp?.length}
          multi
          onBlur={() => {
            updateURL({ mp: selectedMarketplace });
            filtersApi();
            filtersCategory();
            filtersBrand();
            if (selectedApi) {
              body_obj();
            }
          }}
        />

        <DropDownSelector
          options_prop={apiData}
          state={selectedApi}
          setState={setSelectedApi}
          placeholder="Магазин"
          total={totalApi}
          fetchCallback={setApiPage}
          setInputChange={setApiQuery}
          multi
          onBlur={() => {
            updateURL({ api: selectedApi });
            filtersCategory();
            filtersBrand();
            if (selectedApi) {
              body_obj();
            }
          }}
        />

        <DropDownSelector
          options_prop={categoryData}
          state={selectedCategory}
          setState={setSelectedCategory}
          placeholder="Категория"
          total={totalCategory}
          fetchCallback={setCategoryPage}
          setInputChange={setCategoryQuery}
          multi
          onBlur={() => {
            updateURL({ category: selectedCategory });
            filtersBrand();
            if (selectedApi) {
              body_obj();
            }
          }}
        />

        <DropDownSelector
          options_prop={brandData}
          state={selectedBrand}
          setState={setSelectedBrand}
          placeholder="Бренд"
          total={totalBrand}
          fetchCallback={setBrandPage}
          setInputChange={setBrandQuery}
          multi
          onBlur={() => {
            updateURL({ brand: selectedBrand });
            if (selectedApi) {
              body_obj();
            }
          }}
        />
      </FilterContainer>

      <Tabs
        tabs={tabs}
        pageValue={pageValue}
        query={location?.search}
      />

      <div className="anomalies__charts-group">
        <WrapperLineChart
          url={`${KonstantinMasterHttps}/anomaly/${pageValue}/fewer-reviews-than-usual?plot_number=first`}
          body={body_obj()}
          nameChart="Отзывов меньше обычного"
          dependence={pageValue}
          isReady={isReady}
        />

        <WrapperLineChart
          url={`${KonstantinMasterHttps}/anomaly/${pageValue}/fewer-reviews-than-usual?plot_number=second`}
          body={body_obj()}
          nameChart="Отзывов меньше обычного"
          dependence={pageValue}
          isReady={isReady}
        />

        <WrapperLineChart
          url={`${KonstantinMasterHttps}/anomaly/${pageValue}/fewer-reviews-than-usual?plot_number=third`}
          body={body_obj()}
          nameChart="Отзывов меньше обычного"
          dependence={pageValue}
          isReady={isReady}
        />

        <WrapperLineChart
          url={`${KonstantinMasterHttps}/anomaly/${pageValue}/more-reviews-than-usual?plot_number=first`}
          body={body_obj()}
          nameChart="Отзывов больше обычного"
          dependence={pageValue}
          isReady={isReady}
        />

        <WrapperLineChart
          url={`${KonstantinMasterHttps}/anomaly/${pageValue}/more-reviews-than-usual?plot_number=second`}
          body={body_obj()}
          nameChart="Отзывов больше обычного"
          dependence={pageValue}
          isReady={isReady}
        />

        <WrapperLineChart
          url={`${KonstantinMasterHttps}/anomaly/${pageValue}/more-reviews-than-usual?plot_number=third`}
          body={body_obj()}
          nameChart="Отзывов больше обычного"
          dependence={pageValue}
          isReady={isReady}
        />
      </div>

      {pageValue != 'by-categorization' && (
        <div className="anomalies__charts-group">
          <WrapperLineChart
            url={`${KonstantinMasterHttps}/anomaly/${pageValue}/reviews-with-low-ratings?plot_number=first`}
            body={body_obj()}
            nameChart="Отзывов с низкой оценкой (1-3) больше обычного"
            dependence={pageValue}
            isReady={isReady}
          />

          <WrapperLineChart
            url={`${KonstantinMasterHttps}/anomaly/${pageValue}/reviews-with-low-ratings?plot_number=second`}
            body={body_obj()}
            nameChart="Отзывов с низкой оценкой (1-3) больше обычного"
            dependence={pageValue}
            isReady={isReady}
          />

          <WrapperLineChart
            url={`${KonstantinMasterHttps}/anomaly/${pageValue}/reviews-with-low-ratings?plot_number=third`}
            body={body_obj()}
            nameChart="Отзывов с низкой оценкой (1-3) больше обычного"
            dependence={pageValue}
            isReady={isReady}
          />
        </div>
      )}

      {pageValue != 'by-categorization' && (
        <div className="anomalies__charts-group">
          <WrapperLineChart
            url={`${KonstantinMasterHttps}/anomaly/${pageValue}/reviews-with-more-text-than-usual?plot_number=first`}
            body={body_obj()}
            nameChart="Отзывов с текстом больше обычного"
            dependence={pageValue}
            isReady={isReady}
          />

          <WrapperLineChart
            url={`${KonstantinMasterHttps}/anomaly/${pageValue}/reviews-with-more-text-than-usual?plot_number=second`}
            body={body_obj()}
            nameChart="Отзывов с текстом больше обычного"
            dependence={pageValue}
            isReady={isReady}
          />

          <WrapperLineChart
            url={`${KonstantinMasterHttps}/anomaly/${pageValue}/reviews-with-more-text-than-usual?plot_number=third`}
            body={body_obj()}
            nameChart="Отзывов с текстом больше обычного"
            dependence={pageValue}
            isReady={isReady}
          />
        </div>
      )}
    </div>
  );
};

export { Anomalies };
